import { Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// Lote 98 — passo a passo para ver a agenda do IApauta no Google Agenda, Outlook ou Apple
// Calendar por "assinatura" do link (.ics). Aberto pelo botão "Passo a passo" em Configurações e
// pela dica do letreiro do Painel (/config?guia=assinatura).

const PASSOS = {
  google: {
    titulo: "Google Agenda",
    passos: [
      <>No computador, abra o Google Agenda e, ao lado de “Outras agendas”, toque em <b>+</b>.</>,
      <>Escolha <b>De um URL</b>.</>,
      <>Cole o link copiado e toque em <b>Adicionar agenda</b>.</>,
    ],
    nota: "O Google leva de algumas horas até 1 dia para atualizar uma agenda assinada. Depois de adicionada no computador, ela também aparece no celular logado na mesma conta.",
  },
  outlook: {
    titulo: "Outlook",
    passos: [
      <>Abra o Outlook (outlook.com ou novo Outlook) e vá em <b>Calendário</b>.</>,
      <>Toque em <b>Adicionar calendário</b> e escolha <b>Assinar da Web</b>.</>,
      <>Cole o link copiado, dê um nome (por exemplo, IApauta) e toque em <b>Importar</b>.</>,
    ],
    nota: "O Outlook decide de quanto em quanto tempo atualiza; não é instantâneo.",
  },
  apple: {
    titulo: "Apple (Mac e iPhone)",
    passos: [
      <><b>Mac:</b> no app Calendário, vá em <b>Arquivo → Nova assinatura de calendário</b>, cole o link e toque em <b>Assinar</b>.</>,
      <><b>iPhone:</b> Ajustes → Calendário → Contas → <b>Adicionar conta</b> → Outra → <b>Adicionar calendário assinado</b>, cole o link e toque em Seguinte.</>,
    ],
    nota: "A Apple atualiza sozinha de tempos em tempos.",
  },
};

export default function AssinarAgendaGuia({ open, onOpenChange, feedUrl, onCopy }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="assinar-agenda-guia">
        <DialogHeader>
          <DialogTitle>Ver minha agenda do IApauta em outro calendário</DialogTitle>
          <DialogDescription>
            Você assina o link uma única vez e os compromissos aparecem lá, atualizando sozinhos. É só leitura: edições feitas lá não voltam para o IApauta.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0 truncate rounded-md border border-stone-300 bg-stone-50 px-2.5 py-2 font-mono text-xs text-stone-600" data-testid="guia-feed-url">
            {feedUrl || "Gerando link…"}
          </div>
          <Button size="sm" disabled={!feedUrl} onClick={onCopy} data-testid="guia-copiar-link" className="gap-1.5 bg-stone-900 hover:bg-stone-800 text-white">
            <Copy className="w-3.5 h-3.5" strokeWidth={2} /> Copiar link
          </Button>
        </div>
        <Tabs defaultValue="google">
          <TabsList className="grid w-full grid-cols-3 h-auto">
            {Object.entries(PASSOS).map(([k, v]) => (
              <TabsTrigger key={k} value={k} data-testid={`guia-aba-${k}`} className="py-2 text-[13px] font-semibold">{v.titulo}</TabsTrigger>
            ))}
          </TabsList>
          {Object.entries(PASSOS).map(([k, v]) => (
            <TabsContent key={k} value={k} className="mt-3">
              <ol className="list-decimal pl-5 space-y-1.5 text-sm text-stone-800 leading-relaxed">
                {v.passos.map((p, i) => <li key={i}>{p}</li>)}
              </ol>
              <p className="mt-3 rounded-md bg-stone-100 px-3 py-2 text-xs text-stone-600">{v.nota}</p>
            </TabsContent>
          ))}
        </Tabs>
        <p className="text-xs text-stone-500">
          Não compartilhe o link: quem o tiver vê a sua agenda. Se vazar, gere um novo em Configurações (as assinaturas antigas deixam de atualizar).
        </p>
      </DialogContent>
    </Dialog>
  );
}
